import { LayoutGroup } from 'framer-motion';
import { useMemo, useState } from 'react';

import {
  COMPOSITE_SCENARIO_CLIP_SEC,
  type CompositeScenarioEvent,
  type CompositeScenarioMic,
} from '@/data/shared/safetyCompositeScenario';

import CompositeEventTimelineControls from './CompositeEventTimelineControls';
import CompositeEventTimelineFragment from './CompositeEventTimelineFragment';
import TimelineGuides from './TimelineGuides';
import {
  type CompositeGroupingMode,
  groupTimelineEvents,
  timelineTicks,
  verdictSpan,
} from './compositeEventLayout';

const LABEL_COLUMN_CLASS = 'w-[88px] shrink-0 sm:w-[120px]';

type Props = {
  events: ReadonlyArray<CompositeScenarioEvent>;
  mics: ReadonlyArray<CompositeScenarioMic>;
  durationSec: number;
  /** Name of the rule these detections added up to, already translated */
  verdictLabel: string;
  /**
   * The rule name's qualifier ("폭행 의심"), shown in the verdict bar only
   * once the pop-up is wide enough; on a phone the title above carries it.
   */
  verdictQualifier?: string | null;
  t: (key: string, values?: Record<string, string | number>) => string;
};

/**
 * ### CompositeEventTimeline
 *
 * The console's composite-event timeline: a header of second marks, one
 * row per sound type or per microphone, and every detection as a fragment
 * placed where it started, with overlapping detections stacked. The
 * grouping toggle above re-sorts the rows and the fragments glide to their
 * new rows, as in the console.
 *
 * A verdict lane closes the timeline, drawn across the stretch the rule
 * matched over, so the picture says what the detections added up to even when
 * it is read on its own.
 *
 * @param props Detections, microphones, the timeline length, and the translator
 * @returns The rendered timeline element
 */
export default function CompositeEventTimeline({
  events,
  mics,
  durationSec,
  verdictLabel,
  verdictQualifier,
  t,
}: Props) {
  const [mode, setMode] = useState<CompositeGroupingMode>('label');
  const ticks = useMemo(() => timelineTicks(durationSec), [durationSec]);
  const rows = useMemo(
    () => groupTimelineEvents(events, mode, COMPOSITE_SCENARIO_CLIP_SEC),
    [events, mode],
  );
  const micNameById = useMemo(
    () => new Map(mics.map((mic) => [mic.id, t(mic.name)])),
    [mics, t],
  );
  const verdict = useMemo(
    () => verdictSpan(events, COMPOSITE_SCENARIO_CLIP_SEC, durationSec),
    [events, durationSec],
  );

  return (
    <div className="flex w-full flex-col gap-2 overflow-hidden">
      <CompositeEventTimelineControls
        eventCount={events.length}
        durationSec={durationSec}
        mode={mode}
        onModeChange={setMode}
        t={t}
      />

      <LayoutGroup>
        <div className="flex w-full flex-col">
          <div className="flex gap-x-3">
            <div
              className={`${LABEL_COLUMN_CLASS} flex items-center justify-center pr-2 text-xs text-[#696c76]`}
            >
              {t('시간')}
            </div>
            <div className="relative h-6 min-w-0 flex-1">
              <TimelineGuides ticks={ticks} />
              {ticks.map((tick, index) => (
                <span
                  key={`label-${tick.value}`}
                  className={`absolute inset-y-0 items-center text-[10px] font-medium whitespace-nowrap text-[#696c76] ${
                    tick.value % 2 === 0 ? 'flex' : 'hidden sm:flex'
                  }`}
                  style={{
                    left: `${tick.position}%`,
                    transform:
                      index === 0
                        ? 'translateX(2px)'
                        : index === ticks.length - 1
                          ? 'translateX(calc(-100% - 2px))'
                          : 'translateX(-50%)',
                  }}
                >
                  {tick.value}s
                </span>
              ))}
            </div>
          </div>

          {rows.map((row) => {
            const rowName =
              mode === 'label'
                ? t(row.groupKey)
                : (micNameById.get(row.groupKey) ?? row.groupKey);

            return (
              <div
                key={row.groupKey}
                className="flex gap-x-3 transition-colors hover:bg-[rgba(153,156,166,0.15)]"
              >
                <div
                  className={`${LABEL_COLUMN_CLASS} flex items-center gap-1.5 py-1 pr-2`}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-[2px] bg-[#6366f1]"
                    aria-hidden="true"
                  />
                  {/*
                    The English class names run to three words, so a row name
                    wraps rather than being cut to "Female Ag…". It breaks only
                    between words, hyphenating a word that still cannot fit.
                  */}
                  <span className="line-clamp-3 hyphens-auto text-xs font-medium leading-tight text-[#14151a]">
                    {rowName}
                  </span>
                </div>
                <div className="relative min-w-0 flex-1 py-1">
                  <TimelineGuides ticks={ticks} />
                  <div className="relative flex flex-col gap-1">
                    {Array.from({ length: row.layerCount }, (_, layerIndex) => (
                      <div key={layerIndex} className="relative h-6">
                        {row.events
                          .filter(
                            ({ layerIndex: layer }) => layer === layerIndex,
                          )
                          .map(({ event }) => (
                            <CompositeEventTimelineFragment
                              key={event.id}
                              event={event}
                              labelName={t(event.label)}
                              micName={
                                micNameById.get(event.micId) ?? event.micId
                              }
                              leftPercent={(event.startSec / durationSec) * 100}
                              widthPercent={
                                (Math.min(
                                  COMPOSITE_SCENARIO_CLIP_SEC,
                                  durationSec - event.startSec,
                                ) /
                                  durationSec) *
                                100
                              }
                              layerIndex={layerIndex}
                            />
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="mt-1 flex gap-x-3 border-t border-[rgba(153,156,166,0.4)] pt-1">
            <div
              className={`${LABEL_COLUMN_CLASS} flex items-center gap-1.5 py-1 pr-2`}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-[2px] bg-[#dc2626]"
                aria-hidden="true"
              />
              <span className="truncate text-xs font-medium text-[#14151a]">
                {t('판정')}
              </span>
            </div>
            <div className="relative min-w-0 flex-1 py-1">
              <TimelineGuides ticks={ticks} />
              <div className="relative h-6">
                <div
                  className="absolute top-0 flex h-6 items-center justify-center overflow-hidden rounded-[3px] bg-[#dc2626] px-2 shadow-sm"
                  style={{
                    left: `${verdict.leftPercent}%`,
                    width: `${verdict.widthPercent}%`,
                  }}
                >
                  <span className="truncate text-[11px] font-bold text-white">
                    {verdictLabel}
                    {verdictQualifier && (
                      <span className="hidden @md:inline">
                        {` (${verdictQualifier})`}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutGroup>
    </div>
  );
}
